import React, { useState, useEffect, useRef, useCallback } from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { collection, addDoc, serverTimestamp, doc, getDoc, updateDoc } from 'firebase/firestore';
import { ref, uploadBytesResumable, getDownloadURL } from 'firebase/storage';
import { auth, db, storage } from '../firebase';
import { Heart, Flame, Zap, Smile, Ghost, Moon, Megaphone, Frown, CloudRain, Star, MessageCircle, Flower2, AlertTriangle, Sparkles, Camera, Image as ImageIcon, BarChart2, Plus, X, ArrowLeft, Loader2 } from 'lucide-react';

const SHADOW_PLACEHOLDERS = [
  "Spill the tea anonymously...",
  "Who are you secretly crushing on?",
  "What's your biggest regret?",
  "Vent about your ex...",
  "Tell us a secret nobody knows...",
  "What's the wildest rumor you heard?",
  "Confess your academic sins...",
  "Who do you really hate?",
  "What's your weirdest dream?",
  "Just let it all out..."
];

const POLL_PLACEHOLDERS = [
  "Should I text my ex?",
  "Am I the problem?",
  "Forgive or Forget?",
  "Best place to cry on campus?",
  "Is it worth it?",
  "Ghost or Confront?",
  "Red flag or Green flag?",
  "Tell them or keep it secret?",
  "Valid or Delusional?",
  "Love or Career?"
];

const tags = [
  { name: 'Love',       icon: Heart,        color: '#FF6F61' },
  { name: 'Angry',      icon: Flame,        color: '#EF4444' },
  { name: 'Roast',      icon: Zap,          color: '#F97316' },
  { name: 'Crush',      icon: Heart,        color: '#F43F5E' },
  { name: 'Funny',      icon: Smile,        color: '#34D399' },
  { name: 'Regret',     icon: Frown,        color: '#6B7280' },
  { name: 'Secret',     icon: Ghost,        color: '#EC4899' },
  { name: 'Dream',      icon: Moon,         color: '#3B82F6' },
  { name: 'Vent',       icon: Megaphone,    color: '#DC2626' },
  { name: 'Sad',        icon: CloudRain,    color: '#6366F1' },
  { name: 'Hope',       icon: Star,         color: '#22C55E' },
  { name: 'Confession', icon: MessageCircle,color: '#8B5CF6' },
  { name: 'Gratitude',  icon: Flower2,      color: '#F472B6' },
  { name: 'Fear',       icon: AlertTriangle,color: '#9CA3AF' },
  { name: 'Joy',        icon: Sparkles,     color: '#E11D48' },
];

export const CreateShadow = () => {
    const navigate = useNavigate();
    const currentUser = auth.currentUser;

    const [content, setContent] = useState('');
    const [wordCount, setWordCount] = useState(0);
    const [selectedImage, setSelectedImage] = useState(null);
    const [selectedGif, setSelectedGif] = useState(null);
    const [showGifPicker, setShowGifPicker] = useState(false);
    const [gifSearchQuery, setGifSearchQuery] = useState('');
    const [gifResults, setGifResults] = useState([]);
    const [isSearchingGif, setIsSearchingGif] = useState(false);
    const [selectedTags, setSelectedTags] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [tagLimitModal, setTagLimitModal] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    
    const [userCollege, setUserCollege] = useState('');
    const [userIsPremium, setUserIsPremium] = useState(false);
    const [isDataReady, setIsDataReady] = useState(false); 
    
    const [visibility, setVisibility] = useState('global');
    
    const [isPollMode, setIsPollMode] = useState(false);
    const [pollOptions, setPollOptions] = useState([{ text: '' }, { text: '' }]);
    const [placeholderIndex, setPlaceholderIndex] = useState(0);
    const fileInputRef = useRef(null);
    const cameraInputRef = useRef(null);

    const trendingGifs = [
      "https://media.giphy.com/media/l0HlOBZcl7mbVveb6/giphy.gif",
      "https://media.giphy.com/media/xT9IgG50Fb7Mi0prBC/giphy.gif",
      "https://media.giphy.com/media/26AHONQ79FdWZhAI0/giphy.gif",
      "https://media.giphy.com/media/3o7TKSjRrfIPjeiVyM/giphy.gif",
      "https://media.giphy.com/media/l41YtZOb9EUABnuqA/giphy.gif",
      "https://media.giphy.com/media/JIX9t2j0ZTN9S/giphy.gif",
      "https://media.giphy.com/media/cfuL5gqFDreXxkWQ4o/giphy.gif",
      "https://media.giphy.com/media/f31DK1KpGsyMU/giphy.gif",
    ];

    useEffect(() => {
        if (!showGifPicker) return;
        if (gifSearchQuery.trim() === '') {
            setGifResults(trendingGifs);
            return;
        }
        const fetchGifs = async () => {
            setIsSearchingGif(true);
            try {
                const apiKey = process.env.REACT_APP_GIPHY_API_KEY || 'MkLDt7E3pR3qJnqYvjnAbGRTjq5zmQ1g';
                const res = await fetch(`https://api.giphy.com/v1/gifs/search?api_key=${apiKey}&q=${encodeURIComponent(gifSearchQuery)}&limit=20`);
                if (res.ok) {
                    const data = await res.json();
                    const urls = data.data.map(r => r.images.fixed_height.url);
                    setGifResults(urls);
                } else {
                    setGifResults(trendingGifs);
                }
            } catch(e) {
                setGifResults(trendingGifs);
            } finally {
                setIsSearchingGif(false);
            }
        };
        const timeoutId = setTimeout(fetchGifs, 500);
        return () => clearTimeout(timeoutId);
    }, [gifSearchQuery, showGifPicker]);
    
    const [consentModalVisible, setConsentModalVisible] = useState(false);
    const [consentTermsAgreed, setConsentTermsAgreed] = useState(false);
    const [consentResponsibilityAgreed, setConsentResponsibilityAgreed] = useState(false);

    useEffect(() => {
        const fetchUserData = async () => {
            if (currentUser) {
                try {
                    const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
                    if (userDoc.exists()) {
                        const userData = userDoc.data();
                        if (userData.confessionConsent !== true) {
                            setConsentModalVisible(true);
                            // We don't set isDataReady yet, they need to agree
                        } else {
                            setUserCollege(userData.college || '');
                            setUserIsPremium(userData.isPremium || false);
                            setIsDataReady(true);
                        }
                    } else {
                        setIsDataReady(true);
                    }
                } catch (error) {
                    console.error("Error fetching user profile:", error);
                    setIsDataReady(true);
                }
            } else {
                setIsDataReady(true);
            }
        };
        fetchUserData();
    }, [currentUser, navigate]);

    useEffect(() => {
        const words = content.trim().split(/\s+/).filter(word => word.length > 0);
        setWordCount(words.length);
    }, [content]);

    useEffect(() => {
        setPlaceholderIndex(0);
    }, [isPollMode]);

    useEffect(() => {
        if (content.length > 0) return;
        const interval = setInterval(() => {
            const currentList = isPollMode ? POLL_PLACEHOLDERS : SHADOW_PLACEHOLDERS;
            setPlaceholderIndex((prev) => (prev + 1) % currentList.length);
        }, 2500);
        return () => clearInterval(interval);
    }, [content, isPollMode]);

    const handleConsentSubmit = async () => {
        if (!consentTermsAgreed || !consentResponsibilityAgreed) {
            toast.error('Please agree to both conditions.');
            return;
        }
        try {
            await updateDoc(doc(db, 'users', currentUser.uid), { confessionConsent: true });
            setConsentModalVisible(false);
            
            // Re-fetch college since we skipped setting it initially
            const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
            if (userDoc.exists()) {
                setUserCollege(userDoc.data().college || '');
            }
            setIsDataReady(true);
        } catch (err) {
            toast.error('Failed to update consent. Try again.');
        }
    };

    const handlePressConfess = async () => {
        if (!isDataReady) {
            toast.error('Connecting to server... please wait a sec.');
            return;
        }

        if (!currentUser) {
            toast.error('Authentication error. Please restart app.');
            return;
        }

        if (!content.trim()) return toast.error(isPollMode ? 'Ask your question!' : 'Write your shadow!');
        if (wordCount > (isPollMode ? 200 : 1000)) return toast.error('Too long!');
        
        if (isPollMode) {
            const validOptions = pollOptions.filter(o => o.text.trim().length > 0);
            if (validOptions.length < 2) return toast.error('Add at least 2 poll options!');
        }
        if (selectedTags.length === 0) return toast.error('Select at least 1 tag');

        setUploading(true);

        try {
            const tempConfession = {
                college: userCollege,
                content: content.trim(),
                imageUrl: null,
                gifUrl: selectedGif ? selectedGif.url : null,
                createdAt: serverTimestamp(),
                likes: 0,
                shareCount: 0,
                commentCount: 0,
                views: 0,
                userId: currentUser.uid,
                authorIsPremium: userIsPremium,
                tags: selectedTags.map(t => t.name.toLowerCase()),
                displayTags: selectedTags.map(t => t.name),
                visibility: visibility,
                type: isPollMode ? 'poll' : 'regular',
                pollOptions: isPollMode ? pollOptions.map((opt, i) => ({ id: i, text: opt.text.trim(), voteCount: 0 })) : null,
                totalVotes: 0,
            };

            const docRef = await addDoc(collection(db, 'confessions'), tempConfession);

            if (selectedImage) {
                const filename = `confessions/${docRef.id}.jpg`;
                const storageRef = ref(storage, filename);
                await uploadBytesResumable(storageRef, selectedImage);
                const imageUrl = await getDownloadURL(storageRef);
                await updateDoc(docRef, { imageUrl });
            }

            setShowSuccess(true);
            setTimeout(() => {
                navigate('/'); // Redirect to feed
            }, 1500);
        } catch (err) {
            console.error(err);
            toast.error('Failed to post shadow');
        } finally {
            setUploading(false);
        }
    };

    const handleImageChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            setSelectedImage(e.target.files[0]);
            setSelectedGif(null);
            setShowGifPicker(false);
        }
    };

    const handleGifClick = (gifUrl) => {
        setSelectedGif({ url: gifUrl });
        setSelectedImage(null);
        setShowGifPicker(false);
    };

    const handleTagPress = useCallback((tagObj) => {
        const tagName = tagObj.name;
        setSelectedTags((currentTags) => {
            if (currentTags.some(t => t.name === tagName)) {
                return currentTags.filter(t => t.name !== tagName);
            }
            if (currentTags.length >= 2) {
                setTagLimitModal(true);
                return currentTags;
            }
            return [...currentTags, tagObj];
        });
    }, []);

    return (
        <main className="w-full sm:w-[700px] min-h-screen pb-20 sm:pb-0 border-r border-gray-800 flex flex-col shrink-0 bg-[#0a0a0a] overflow-x-hidden">
            {/* Success Toast */}
            {showSuccess && (
                <div className="fixed bottom-10 left-1/2 -translate-x-1/2 z-50 animate-fade-in-up flex items-center gap-3 bg-white text-black px-6 py-3 rounded-full shadow-2xl">
                    <Sparkles size={20} className="text-[#0BD9E5]" />
                    <span className="font-bold text-[15px]">Shadow Posted</span>
                    <button onClick={() => setShowSuccess(false)} className="ml-2 text-gray-500 hover:text-black">
                        <X size={16} />
                    </button>
                </div>
            )}

            {/* Header */}
            <div className="sticky top-0 bg-[#0a0a0a]/90 backdrop-blur-md z-40 border-b border-[#1a1a1a] flex items-center h-[60px] px-4 justify-between">
                <div className="flex items-center">
                    <button 
                        onClick={() => navigate(-1)}
                        className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors mr-2"
                    >
                        <X size={24} className="text-white" />
                    </button>
                    <h1 className="text-xl font-bold text-white">Create Shadow</h1>
                </div>
                <button
                    onClick={handlePressConfess}
                    disabled={uploading || !content.trim() || wordCount > 1000 || selectedTags.length === 0 || !isDataReady || (isPollMode && pollOptions.filter(o => o.text.trim().length > 0).length < 2)}
                    className="bg-[#0BD9E5] hover:bg-[#09bcc7] text-black font-bold px-5 py-2 rounded-full disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                    {uploading ? 'Posting...' : 'Post'}
                </button>
            </div>

            <div className="flex-1 p-5 overflow-y-auto">
                <div className="mb-6">
                    <div className="flex justify-between items-center mb-3">
                        <label className="text-lg font-semibold text-white">
                            {isPollMode ? "Ask a question" : "Your Shadow"}
                        </label>
                        <button 
                            onClick={() => setIsPollMode(!isPollMode)}
                            className={`flex items-center gap-2 px-3 py-1.5 rounded-lg transition-colors ${isPollMode ? 'bg-[#0BD9E5]/10 text-[#0BD9E5]' : 'hover:bg-white/5 text-gray-400'}`}
                        >
                            <BarChart2 size={18} />
                            <span className="font-bold">Poll</span>
                        </button>
                    </div>

                    <div className="relative">
                        <textarea
                            className={`w-full bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-4 text-white focus:outline-none focus:border-[#0BD9E5] resize-none ${isPollMode ? 'h-[120px] text-xl font-bold' : 'h-[280px] text-base'}`}
                            value={content}
                            onChange={(e) => setContent(e.target.value)}
                            placeholder={(isPollMode ? POLL_PLACEHOLDERS : SHADOW_PLACEHOLDERS)[placeholderIndex]}
                            maxLength={isPollMode ? 200 : undefined}
                        />
                        <div className={`text-right mt-2 text-sm ${wordCount > (isPollMode ? 200 : 1000) ? 'text-red-500' : 'text-gray-400'}`}>
                            {wordCount}/{isPollMode ? 200 : 1000} words (approx)
                        </div>
                    </div>
                </div>

                {/* Poll Inputs Section */}
                {isPollMode && (
                    <div className="mb-6">
                        <label className="text-lg font-semibold text-white block mb-3">Poll Options</label>
                        {pollOptions.map((opt, index) => (
                            <div key={index} className="flex items-center gap-3 mb-3">
                                <input
                                    type="text"
                                    className="flex-1 bg-[#1a1a1a] border border-[#333] rounded-xl p-3 text-white focus:outline-none focus:border-[#0BD9E5]"
                                    placeholder={`Option ${index + 1}`}
                                    value={opt.text}
                                    onChange={(e) => {
                                        const newOpts = [...pollOptions];
                                        newOpts[index].text = e.target.value;
                                        setPollOptions(newOpts);
                                    }}
                                    maxLength={30}
                                />
                                {pollOptions.length > 2 && (
                                    <button onClick={() => setPollOptions(pollOptions.filter((_, i) => i !== index))} className="p-2 hover:bg-white/10 rounded-full">
                                        <X size={20} className="text-red-500" />
                                    </button>
                                )}
                            </div>
                        ))}
                        {pollOptions.length < 6 && (
                            <button 
                                onClick={() => setPollOptions([...pollOptions, { text: '' }])}
                                className="flex items-center justify-center gap-2 w-full p-3 border border-dashed border-[#0BD9E5] rounded-xl text-[#0BD9E5] hover:bg-[#0BD9E5]/5 transition-colors mt-2 font-bold"
                            >
                                <Plus size={18} />
                                Add Option
                            </button>
                        )}
                    </div>
                )}

                {/* Media Buttons */}
                {!selectedImage && !selectedGif ? (
                    <div className="flex flex-wrap sm:flex-nowrap gap-2 sm:gap-3 mb-6 relative">
                        <input type="file" accept="image/*,video/*" capture="environment" className="hidden" ref={cameraInputRef} onChange={handleImageChange} />
                        <input type="file" accept="image/*,video/*" className="hidden" ref={fileInputRef} onChange={handleImageChange} />
                        <button onClick={() => cameraInputRef.current?.click()} className="flex-1 min-w-[90px] flex items-center justify-center gap-1.5 sm:gap-2 bg-[#1a1a1a] border border-[#2a2a2a] p-2.5 sm:p-4 rounded-xl text-white font-semibold hover:bg-[#222] transition-colors text-xs sm:text-sm">
                            <Camera size={18} />
                            Camera
                        </button>
                        <button onClick={() => fileInputRef.current?.click()} className="flex-1 min-w-[90px] flex items-center justify-center gap-1.5 sm:gap-2 bg-[#1a1a1a] border border-[#2a2a2a] p-2.5 sm:p-4 rounded-xl text-white font-semibold hover:bg-[#222] transition-colors text-xs sm:text-sm">
                            <ImageIcon size={18} />
                            Gallery
                        </button>
                        <button onClick={() => setShowGifPicker(!showGifPicker)} className="flex-1 min-w-[90px] flex items-center justify-center gap-1.5 sm:gap-2 bg-[#1a1a1a] border border-[#2a2a2a] p-2.5 sm:p-4 rounded-xl text-white font-semibold hover:bg-[#222] transition-colors text-xs sm:text-sm">
                            <span className="font-extrabold text-[10px] sm:text-[12px] border border-white px-1 py-0.5 rounded">GIF</span>
                            Search
                        </button>

                        {showGifPicker && (
                            <div className="absolute top-[100%] left-0 right-0 mt-2 z-50 bg-[#111] border border-gray-800 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-72">
                                <div className="p-3 border-b border-gray-800 flex justify-between items-center bg-black/50 backdrop-blur-md">
                                    <input 
                                        type="text" 
                                        placeholder="Search GIFs..." 
                                        value={gifSearchQuery}
                                        onChange={(e) => setGifSearchQuery(e.target.value)}
                                        className="bg-transparent border-none text-white text-sm focus:outline-none w-full mr-2"
                                        autoFocus
                                    />
                                    <button onClick={() => setShowGifPicker(false)} className="text-gray-500 hover:text-white shrink-0"><X size={16}/></button>
                                </div>
                                <div className="p-2 grid grid-cols-2 gap-2 overflow-y-auto custom-scrollbar relative" style={{ minHeight: '150px' }}>
                                   {isSearchingGif ? (
                                       <div className="absolute inset-0 flex items-center justify-center">
                                           <Loader2 size={24} className="text-[#0BD9E5] animate-spin" />
                                       </div>
                                   ) : (
                                       gifResults.map((gifUrl, i) => (
                                           <img 
                                              key={i} src={gifUrl} alt="gif" 
                                              onClick={() => handleGifClick(gifUrl)}
                                              className="w-full h-24 object-cover rounded-lg cursor-pointer hover:opacity-80 transition-opacity border border-gray-800"
                                           />
                                       ))
                                   )}
                                </div>
                            </div>
                        )}
                    </div>
                ) : (
                    <div className="relative max-w-full w-fit mb-6">
                        <img src={selectedImage ? URL.createObjectURL(selectedImage) : selectedGif.url} alt="Preview" className="max-w-full w-[300px] h-[300px] object-cover rounded-2xl border border-[#333]" />
                        <button onClick={() => { setSelectedImage(null); setSelectedGif(null); }} className="absolute top-3 right-3 w-8 h-8 bg-black/80 rounded-full flex items-center justify-center border border-[#333] hover:bg-black transition-colors">
                            <X size={16} className="text-white" />
                        </button>
                    </div>
                )}

                {/* Tags */}
                <div className="mb-6">
                    <label className="text-lg font-semibold text-white block mb-3">Tags (min 1, max 2)</label>
                    <div className="grid grid-cols-3 sm:flex sm:flex-wrap gap-2 sm:gap-3">
                        {tags.map(tag => {
                            const isSelected = selectedTags.some(t => t.name === tag.name);
                            const Icon = tag.icon;
                            return (
                                <button
                                    key={tag.name}
                                    onClick={() => handleTagPress(tag)}
                                    style={{ backgroundColor: isSelected ? tag.color : '#2a2a2a' }}
                                    className={`flex items-center justify-center gap-1 sm:gap-2 px-2 sm:px-4 py-2 sm:py-2.5 rounded-full border transition-colors text-xs sm:text-sm ${isSelected ? 'border-transparent text-black font-bold' : 'border-[#444] text-gray-300 hover:border-gray-500'}`}
                                >
                                    <span className="truncate">{tag.name}</span>
                                    <Icon size={14} color={isSelected ? '#000' : '#ccc'} className="shrink-0" />
                                </button>
                            );
                        })}
                    </div>
                </div>

                {/* Visibility */}
                <div className="mb-8 border-t border-[#1a1a1a] pt-6">
                    <label className="text-gray-400 text-sm block mb-3">Who can see this?</label>
                    <div className="flex gap-4">
                        {['global', 'campus'].map(option => {
                            const isSelected = visibility === option;
                            const isDisabled = option === 'campus' && !userCollege;
                            return (
                                <button
                                    key={option}
                                    disabled={isDisabled}
                                    onClick={() => setVisibility(option)}
                                    className={`flex items-center gap-2 px-4 py-2 rounded-full border transition-colors ${isSelected ? 'border-[#0BD9E5] bg-[#0BD9E5]/10' : 'border-[#333] bg-white/5 hover:bg-white/10'} ${isDisabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                                >
                                    <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${isSelected ? 'border-[#0BD9E5]' : 'border-gray-500'}`}>
                                        {isSelected && <div className="w-2 h-2 rounded-full bg-[#0BD9E5]" />}
                                    </div>
                                    <span className={isSelected ? 'text-white font-semibold' : 'text-gray-400'}>
                                        {option === 'global' ? 'Global' : 'Campus'}
                                    </span>
                                </button>
                            );
                        })}
                    </div>
                    <p className="text-gray-500 text-sm mt-3">
                        {visibility === 'global' ? "Everyone on Spark can see this shadow" : `Only people from ${userCollege || 'your campus'} can see this`}
                    </p>
                </div>
            </div>

            {/* Tag Limit Modal */}
            {tagLimitModal && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                    <div className="bg-[#161616] border border-[#333] rounded-2xl p-6 max-w-sm w-full text-center">
                        <h3 className="text-white text-lg font-bold mb-6">Maximum 2 tags allowed!</h3>
                        <button onClick={() => setTagLimitModal(false)} className="bg-white text-black font-bold px-8 py-2.5 rounded-full hover:bg-gray-200 transition-colors">
                            OK
                        </button>
                    </div>
                </div>
            )}

            {/* Consent Modal */}
            {consentModalVisible && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
                    <div className="bg-[#161616] border border-[#222] rounded-3xl p-6 max-w-sm w-full">
                        <h3 className="text-white text-xl font-bold mb-4">Community Guidelines</h3>
                        <p className="text-gray-300 text-[15px] leading-relaxed mb-6">
                            To continue using shadows, please agree:<br/><br/>
                            • No harmful, offensive, or illegal content<br/>
                            • Keep it fun avoid posts that encourage harm<br/>
                            • All shadows & comments are 100% anonymous<br/>
                            • Report bad stuff to keep the vibe safe<br/>
                            • Breaking rules = removal or ban<br/>
                            • You are responsible for what you post
                        </p>
                        <div className="flex flex-col gap-4 mb-6">
                            <label className="flex items-center gap-3 cursor-pointer">
                                <input type="checkbox" className="w-5 h-5 accent-[#0BD9E5]" checked={consentTermsAgreed} onChange={(e) => setConsentTermsAgreed(e.target.checked)} />
                                <span className="text-white text-sm">I agree to the community guidelines</span>
                            </label>
                            <label className="flex items-center gap-3 cursor-pointer">
                                <input type="checkbox" className="w-5 h-5 accent-[#0BD9E5]" checked={consentResponsibilityAgreed} onChange={(e) => setConsentResponsibilityAgreed(e.target.checked)} />
                                <span className="text-white text-sm">I take responsibility for my posts</span>
                            </label>
                        </div>
                        <button 
                            onClick={handleConsentSubmit}
                            className="w-full bg-[#0BD9E5] text-black font-bold py-3 rounded-full hover:bg-[#09bcc7] transition-colors"
                        >
                            Agree & Continue
                        </button>
                    </div>
                </div>
            )}
        </main>
    );
};

export default CreateShadow;
